// landingpage.jsx

import { motion } from "framer-motion"; // Motion/Framer Motion animations [web:11][web:20]
import { Link, useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel"; // Modern responsive carousel [web:19][web:21]
import "react-multi-carousel/lib/styles.css";
import React, { useRef } from "react";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 640 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 640, min: 0 },
    items: 1,
  },
};

const slideImages = [
  "/images/hero 1.png",
  "/images/hero 2.png",
];


// Animation variants for sequential, unique effects
const containerVariant = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.25,
    },
  },
};

const textVariant = {
  hidden: { x: -80, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.7, delay: 0.1, type: "spring", stiffness: 80, damping: 18 },
  },
};

const badgeVariant = {
  hidden: { scale: 0.5, rotate: -20, opacity: 0 },
  visible: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: { duration: 0.7, delay: 0.5, type: "spring", stiffness: 200, damping: 10 },
  },
};

const buttonVariant = {
  hidden: { y: 40, opacity: 0, scale: 0.9 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, delay: 0.7, type: "spring", stiffness: 250 },
  },
};

const sliderControlsVariant = {
  hidden: { y: 40, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, delay: 1, type: "spring", stiffness: 180 },
  },
};

const rightSectionVariant = {
  hidden: { x: 80, opacity: 0, scale: 0.95 },
  visible: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, delay: 1.2, type: "spring", stiffness: 70, damping: 16 },
  },
};

const carouselImageVariant = {
  hidden: { scale: 0.92, opacity: 0, y: 40 },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 1.5, type: "spring", stiffness: 120 },
  },
};

const experienceBadgeVariant = {
  hidden: { y: 60, opacity: 0, scale: 0.8 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, delay: 2, type: "spring", stiffness: 120 },
  },
};


function LandingPage() {
  const navigate = useNavigate();
  const carouselRef = useRef(null);

  const handlePrev = () => {
    if (carouselRef.current) {
      carouselRef.current.previous();
    }
  };

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-[var(--bg)] text-white overflow-hidden"
      style={{
        paddingTop: '-64px',
        Height: '50vh',
        boxSizing: 'border-box',
      }}
      variants={containerVariant}
      initial="hidden"
      animate="visible"
    >
      {/* Main layout */}
      <div className="mx-auto flex max-w-7xl flex-col lg:flex-row min-h-[calc(100vh-64px)]">
        {/* Left Section */}
        <motion.section
          className="relative w-full bg-[var(--bg)] px-6 py-10 sm:px-10 lg:w-1/2 lg:pb-10"
          variants={containerVariant}
        >
          <motion.span
            className="mb-4 inline-block rounded-full bg-[var(--bg3)] px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-black"
            variants={badgeVariant}
            whileHover={{ scale: 1.08, rotate: 2 }}
            transition={{ type: "spring", stiffness: 250 }}
          >
            Integrated MEP Design
          </motion.span>

          <motion.h1
            className="text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl"
            variants={textVariant}
          >
            <span className="block text-white">Engineering  </span>
            <span className="block text-[var(--bg3)]">Design</span>
            <span className="block">Delivery</span>
          </motion.h1>

          <motion.p
            className="mt-6 max-w-md text-sm text-gray-300 sm:text-base"
            variants={textVariant}
          >
            Skyline MEP Consultants deliver integrated MEP solutions across residential, commercial, industrial, hospitality, and healthcare projects.
          </motion.p>

          {/* CTA + slider controls mimic */}
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-6"
            variants={containerVariant}
          >
            <Link
              to="/projects"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
              }}
            >
              <motion.button
                className="flex items-center gap-3 rounded-none bg-[var(--bg3)] px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-black"
                variants={buttonVariant}
                whileHover={{
                  scale: 1.06,
                  x: 4,
                  boxShadow: "0 12px 30px rgba(131, 131, 131, 0.45)",
                }}
                whileTap={{ scale: 0.96, x: 0 }}
                transition={{ type: "spring", stiffness: 250 }}
              >
                See all Projects
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--bg)] text-yellow-400">
                  →
                </span>
              </motion.button>
            </Link>

            <motion.div
              className="flex items-center gap-4 text-sm text-gray-300"
              variants={sliderControlsVariant}
            >
              <div className="flex gap-3">
                <motion.button
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-900"
                  whileHover={{ scale: 1.08, rotate: -10 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Previous slide"
                  onClick={handlePrev}
                >
                  ←
                </motion.button>
                <motion.button
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg3)] text-black"
                  whileHover={{ scale: 1.08, rotate: 10 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Next slide"
                  onClick={handleNext}
                >
                  →
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Right Section with slider */}
        <motion.section
          className="relative w-full lg:w-1/2"
          variants={rightSectionVariant}
        >
          <div className="relative h-[320px] sm:h-[420px] md:h-[480px] lg:h-full">
            <Carousel
              ref={carouselRef}
              responsive={responsive}
              autoPlay
              autoPlaySpeed={5000}
              infinite
              showDots={false}
              arrows={false}
              swipeable
              draggable
              keyBoardControl
              customTransition="transform 700ms ease-out"
              transitionDuration={700}
              containerClass="h-full"
              itemClass="h-full"
            >
              {slideImages.map((src, index) => (
                <motion.div
                  key={index}
                  className="h-full w-full"
                  variants={carouselImageVariant}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 120 }}
                >
                  <div className="relative h-full w-full">
                    <img
                      src={src}
                      alt={`Apartment ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                    <div className="pointer-events-none absolute inset-0" />
                  </div>
                </motion.div>
              ))}
            </Carousel>

            {/* Experience badge */}
            <motion.div
              className="pointer-events-none absolute bottom-8 left-1/2 z-20 -translate-x-1/2 sm:left-8 sm:translate-x-0"
              variants={experienceBadgeVariant}
              initial="hidden"
              animate="visible"
            >
              <div className="bg-[var(--bg3)] px-10 py-8 text-black shadow-2xl">
                <div className="text-4xl font-extrabold leading-none">800+</div>
                <div className="mt-2 text-[0.8rem] font-semibold tracking-[0.3em] uppercase">
                  Projects
                  <br />
                  Delivered
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>

    </motion.div>
  );
}

export default LandingPage;
